// src/features/business-functions/components/workspaces/WorkspacesTab.jsx
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useRecoilValue } from 'recoil';
import { CAMPAIGN_COPY } from '../../constants/campaignCopy';
import useCanCreateWorkspace from '../../hooks/useCanCreateWorkspace';
import { authzContextAtom } from '../../../../authorization/authzState';
import { accountService } from '../../services/accountService';
import { userInformationAtom } from '../../../../Services/_states';
import { ConstantActions } from '../../../../Services/_actions/index.js';
import { useSnackBar } from '../../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import { canCreateBusinessFunction } from '../../utils/canCreateBusinessFunction';
import { canManageWorkspaceMappings } from '../../utils/canManageWorkspaceMappings';
import { isWorkspaceActive } from './WorkspaceCard';
import WorkspaceCardGrid from './WorkspaceCardGrid';
import WorkspaceEmptyState from './WorkspaceEmptyState';
import CreateBusinessServiceDrawer from './CreateBusinessServiceDrawer';
import WorkspaceMappingsTab from '../workspace-detail/WorkspaceMappingsTab';

function readToggleError(err) {
  const raw =
    err?.response?.data?.data?.general_error ||
    err?.response?.data?.data?.system_error ||
    err?.response?.data?.message ||
    err?.message ||
    CAMPAIGN_COPY.list.toggleFailed;
  return typeof raw === 'string' ? raw : CAMPAIGN_COPY.list.toggleFailed;
}

/**
 * Business Services tab — workspace card grid with create CTA.
 */
export default function WorkspacesTab({
  accountId,
  accountName,
  organisationId,
  workspaces,
  isLoading,
  error,
  styles,
  onWorkspaceCreated,
  onWorkspacesChange,
}) {
  const { showSnackBar } = useSnackBar();
  const userInfo = useRecoilValue(userInformationAtom);
  const authz = useRecoilValue(authzContextAtom);
  const { userHasPermission } = ConstantActions();
  const canCreate = useCanCreateWorkspace(accountId);
  const canToggle = canCreateBusinessFunction(userInfo);
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [mappingWorkspace, setMappingWorkspace] = useState(null);
  const [togglingWorkspaceId, setTogglingWorkspaceId] = useState('');
  const [localWorkspaces, setLocalWorkspaces] = useState(workspaces);

  useEffect(() => {
    setLocalWorkspaces(workspaces);
  }, [workspaces]);

  const visibleWorkspaces = canToggle
    ? localWorkspaces
    : (localWorkspaces || []).filter((workspace) => isWorkspaceActive(workspace));
  const hasWorkspaces = visibleWorkspaces?.length > 0;
  const canMapWorkspace = useCallback(
    (workspace) => canManageWorkspaceMappings(authz, {
      workspaceId: workspace?.id,
      accountId,
      userInfo,
      legacyUserHasPermission: userHasPermission,
    }),
    [accountId, authz, userInfo, userHasPermission]
  );

  const openCreateDrawer = useCallback(() => {
    if (!canCreate) return;
    setCreateDrawerOpen(true);
  }, [canCreate]);

  const closeCreateDrawer = useCallback(() => {
    setCreateDrawerOpen(false);
  }, []);

  const handleCreated = useCallback(async (created) => {
    await onWorkspaceCreated?.(created);
  }, [onWorkspaceCreated]);

  const patchWorkspaceInList = useCallback((workspaceId, patch) => {
    setLocalWorkspaces((prev) =>
      (prev || []).map((workspace) =>
        workspace.id === workspaceId ? { ...workspace, ...patch } : workspace
      )
    );
  }, []);

  const handleToggle = useCallback(async (workspace, nextDisabled) => {
    if (!accountId || !workspace?.id || !canToggle) return;

    const previousDisabled = Boolean(workspace.isDisabled);
    if (previousDisabled === nextDisabled) return;

    setTogglingWorkspaceId(workspace.id);
    patchWorkspaceInList(workspace.id, { isDisabled: nextDisabled });

    try {
      const res = await accountService.setWorkspaceDisabled(
        accountId,
        workspace.id,
        nextDisabled
      );
      if (res?.status !== 200 || res?.data?.status === false) {
        throw new Error(
          res?.data?.data?.general_error || CAMPAIGN_COPY.list.toggleFailed
        );
      }
      showSnackBar(
        nextDisabled
          ? CAMPAIGN_COPY.list.deactivated
          : CAMPAIGN_COPY.list.activated,
        'success'
      );
      await onWorkspacesChange?.();
    } catch (err) {
      patchWorkspaceInList(workspace.id, { isDisabled: previousDisabled });
      showSnackBar(readToggleError(err), 'error');
    } finally {
      setTogglingWorkspaceId('');
    }
  }, [
    accountId,
    canToggle,
    onWorkspacesChange,
    patchWorkspaceInList,
    showSnackBar,
  ]);

  const newButton = (
    <Button
      variant="contained"
      startIcon={<AddIcon />}
      sx={styles.workspacesNewBtn}
      disableElevation
      disabled={!canCreate}
      onClick={openCreateDrawer}
    >
      {CAMPAIGN_COPY.list.newButton}
    </Button>
  );

  return (
    <Box>
      <Box sx={{...styles.workspacesHeaderRoot , mt:0}}>
        <Box sx={styles.workspacesHeaderLeft}>
          <Typography sx={styles.workspacesTitle}>
            {CAMPAIGN_COPY.list.titleInAccount(accountName)}
          </Typography>
          <Typography sx={styles.workspacesSubtitle}>
            {CAMPAIGN_COPY.list.subtitle}
          </Typography>
        </Box>

        {canCreate ? (
          newButton
        ) : (
          <Tooltip title={CAMPAIGN_COPY.list.newButtonDenied} arrow placement="top">
            <span>{newButton}</span>
          </Tooltip>
        )}
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {isLoading && !hasWorkspaces ? (
        <WorkspaceCardGrid
          accountId={accountId}
          accountName={accountName}
          workspaces={[]}
          styles={styles}
          isLoading={true}
        />
      ) : hasWorkspaces ? (
        <WorkspaceCardGrid
          accountId={accountId}
          accountName={accountName}
          workspaces={visibleWorkspaces}
          canToggle={canToggle}
          togglingWorkspaceId={togglingWorkspaceId}
          onToggle={handleToggle}
          onMappingClick={(workspace) => {
            if (canMapWorkspace(workspace)) setMappingWorkspace(workspace);
          }}
          canManageMappings={canMapWorkspace}
          styles={styles}
          isLoading={false}
        />
      ) : (
        <WorkspaceEmptyState styles={styles} error={error} />
      )}

      {mappingWorkspace && canMapWorkspace(mappingWorkspace) ? (
        <Box sx={{ mt: 3, p: { xs: 1.5, sm: 2 }, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
          <WorkspaceMappingsTab
            workspace={mappingWorkspace}
            onClose={() => setMappingWorkspace(null)}
          />
        </Box>
      ) : null}

      <CreateBusinessServiceDrawer
        open={createDrawerOpen}
        onClose={closeCreateDrawer}
        accountId={accountId}
        organisationId={organisationId}
        onCreated={handleCreated}
      />
    </Box>
  );
}
